import React, { useContext, useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import topApi from '../../../components/api/ToPApi';
import Paginator from '../../../components/ui/Paginator';
import ReportDateRange from '../../../components/ui/ReportDateRange';
import MerchantPicker from '../../../components/ui/MerchantPicker';
import { AuthContext } from '../../../context/auth';
import { errorMessage, isPlatformUser } from '../../../utils/access';

// Seguimiento de intentos de pago con tarjeta (3-D Secure): link de pago, menú QR de la mesa, terceros y app.
// API: GET /payment-attempts (listado) y GET /payment-attempts/:attemptId (detalle con historial).
// Solo plataforma: el menú no se asigna a bancos ni comercios y el API responde 403 a los demás.

interface PaymentAttempt {
    attemptKey: string;
    merchantId: number | null;
    merchantName: string | null;
    channel: string;
    operation: string;
    processor: string | null;
    clientId: string | null;
    orderId: string | null;
    providerTransactionId: string | null;
    transactionId: number | null;
    linkToken: string | null;
    tableId: number | null;
    localOrderId: number | null;
    amount: string | number | null;
    currency: string | null;
    pan: string | null;
    description: string | null;
    status: string;
    step: string;
    authCode: string | null;
    errorCode: string | null;
    errorMessage: string | null;
    followUpStatus: string | null;
    followUpDetail: string | null;
    createdAt: string;
    updatedAt: string;
    finishedAt: string | null;
}

interface AttemptEvent { event: string; status: string | null; detail: string | null; createdAt: string; }

interface ListResponse {
    attempts: PaymentAttempt[];
    total: number;
    summary: { byStatus: Record<string, number>; followUpErrors: number };
}

const PAGE_SIZE = 25;

const STATUS: Record<string, { label: string; badge: string }> = {
    STARTED: { label: 'Iniciado', badge: 'text-bg-secondary' },
    CHALLENGE: { label: 'En verificación 3DS', badge: 'text-bg-info' },
    PENDING: { label: 'Sin confirmar', badge: 'text-bg-warning' },
    APPROVED: { label: 'Aprobado', badge: 'text-bg-success' },
    AUTHORIZED: { label: 'Autorizado', badge: 'text-bg-success' },
    DECLINED: { label: 'Rechazado', badge: 'text-bg-danger' },
    EXPIRED: { label: 'Expirado', badge: 'text-bg-secondary' },
    ERROR: { label: 'Error técnico', badge: 'text-bg-danger' },
};

const CHANNEL: Record<string, { label: string; icon: string }> = {
    LINK: { label: 'Link de pago', icon: 'bi-link-45deg' },
    MENU_QR: { label: 'Menú QR (mesa)', icon: 'bi-qr-code' },
    EXTERNAL: { label: 'Tercero', icon: 'bi-plug' },
    APP: { label: 'App / POS', icon: 'bi-phone' },
};

const EVENT_LABEL: Record<string, string> = {
    creado: 'Intento creado',
    inicio: 'Respuesta del procesador',
    desafio_abierto: 'El comprador entró a la verificación 3DS',
    desafio: 'Regreso del banco (3DS)',
    retorno: 'Regreso del comprador',
    link_marcado: 'Marcar link como pagado',
    orden_mesa: 'Crear orden de la mesa',
    tercero_avisado: 'Aviso al tercero',
};

const eventLabel = (event: string) =>
    EVENT_LABEL[event] ?? (event.startsWith('webhook:') ? `Webhook ${event.slice(8)}` : event);

const formatAmount = (amount: any, currency: string | null) => {
    const value = Number(amount);
    if (amount === null || amount === undefined || isNaN(value)) return '-';
    return `${currency ?? ''} ${value.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
};

// Las fechas llegan en UTC: se muestran en la hora local.
const formatDate = (value: string | null) => value ? moment.utc(value).local().format('DD/MM/YYYY HH:mm:ss') : '-';

const parseDetail = (detail: string | null): any => {
    if (!detail) return null;
    try { return JSON.parse(detail); } catch { return detail; }
};

const StatusBadge = ({ status }: { status: string }) => {
    const info = STATUS[status] ?? { label: status, badge: 'text-bg-light' };
    return <span className={`badge ${info.badge}`}>{info.label}</span>;
};

const FollowUpBadge = ({ status }: { status: string | null }) => {
    if (status === 'OK') return <span className="badge text-bg-success-subtle text-success-emphasis border border-success-subtle">Completado</span>;
    if (status === 'ERROR') return <span className="badge text-bg-danger-subtle text-danger-emphasis border border-danger-subtle"><i className="bi bi-exclamation-triangle me-1" />Falló</span>;
    return <span className="text-muted small">-</span>;
};

type Filters = {
    from: Moment;
    to: Moment;
    merchantId: number;
    status: string;
    channel: string;
    followUpStatus: string;
    reference: string;
};

// La ruta no tiene guardia propia (se puede abrir por URL): solo usuarios de plataforma ven el reporte.
export const PaymentAttemptsPage = () => {
    const { user } = useContext(AuthContext);

    if (!isPlatformUser(user)) {
        return (
            <div className="mt-5 container-fluid px-4" style={{ flex: 1 }}>
                <div className="alert alert-warning">No tiene acceso a este reporte.</div>
            </div>
        );
    }

    return <PaymentAttemptsReport />;
};

const PaymentAttemptsReport = () => {
    const [filters, setFilters] = useState<Filters>({
        from: moment().startOf('day'),
        to: moment().endOf('day'),
        merchantId: -1,
        status: '',
        channel: '',
        followUpStatus: '',
        reference: '',
    });
    const [reference, setReference] = useState('');
    const [page, setPage] = useState(1);
    const [data, setData] = useState<ListResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        setError('');
        topApi.get<ListResponse>('/payment-attempts', {
            params: {
                from: filters.from.toISOString(),
                to: filters.to.toISOString(),
                merchantId: filters.merchantId > 0 ? filters.merchantId : undefined,
                status: filters.status || undefined,
                channel: filters.channel || undefined,
                followUpStatus: filters.followUpStatus || undefined,
                reference: filters.reference || undefined,
                pageIndex: page - 1,
                pageSize: PAGE_SIZE,
            },
        })
            .then(resp => setData(resp.data))
            .catch(e => {
                // 404: el API al que apunta el portal aún no tiene el reporte (no se ha desplegado).
                setError(e?.response?.status === 404
                    ? 'El servidor no tiene disponible el reporte de intentos de pago. Verifique que el API esté actualizado.'
                    : errorMessage(e));
                setData(null);
            })
            .finally(() => setLoading(false));
    }, [filters, page]);

    const update = (changes: Partial<Filters>) => {
        setPage(1);
        setFilters(prev => ({ ...prev, ...changes }));
    };

    const byStatus = data?.summary.byStatus ?? {};
    const count = (...statuses: string[]) => statuses.reduce((sum, s) => sum + (byStatus[s] ?? 0), 0);
    const totalAll = Object.values(byStatus).reduce((sum, n) => sum + n, 0);
    const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

    // Tarjetas de resumen: al hacer clic filtran el listado.
    const tiles = [
        { label: 'Intentos', value: totalAll, filter: { status: '', followUpStatus: '' }, tone: '' },
        { label: 'Aprobados', value: count('APPROVED', 'AUTHORIZED'), filter: { status: 'APPROVED,AUTHORIZED', followUpStatus: '' }, tone: 'text-success' },
        { label: 'Rechazados', value: count('DECLINED', 'EXPIRED'), filter: { status: 'DECLINED,EXPIRED', followUpStatus: '' }, tone: 'text-danger' },
        { label: 'En curso o sin confirmar', value: count('STARTED', 'CHALLENGE', 'PENDING'), filter: { status: 'STARTED,CHALLENGE,PENDING', followUpStatus: '' }, tone: 'text-warning-emphasis' },
        { label: 'Errores técnicos', value: count('ERROR'), filter: { status: 'ERROR', followUpStatus: '' }, tone: 'text-danger' },
        { label: 'Cobrados con acción fallida', value: data?.summary.followUpErrors ?? 0, filter: { status: '', followUpStatus: 'ERROR' }, tone: 'text-danger' },
    ];
    const isActive = (filter: { status: string; followUpStatus: string }) =>
        filters.status === filter.status && filters.followUpStatus === filter.followUpStatus;

    return (
        <div className="mt-5 container-fluid px-4" style={{ flex: 1, marginTop: 10 }}>
            <div className="mb-4">
                <h3 className="mb-1">Intentos de pago</h3>
                <p className="text-muted mb-0">
                    Cada intento de pago con tarjeta (link de pago, menú QR de la mesa, terceros y app) y qué pasó en cada paso:
                    verificación 3DS, respuesta del banco y la acción posterior al cobro.
                </p>
            </div>

            {/* FILTROS */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                    <div className="row g-2 align-items-end">
                        <div className="col-md-6 col-lg-3">
                            <label className="form-label small text-muted mb-1">Fecha</label>
                            <ReportDateRange
                                minDate={moment().subtract(1, 'year')}
                                onChange={(start, end) => update({ from: start.clone().startOf('day'), to: end.clone().endOf('day') })} />
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <label className="form-label small text-muted mb-1" htmlFor="attemptMerchant">Comercio</label>
                            <MerchantPicker id="attemptMerchant" value={filters.merchantId} onChange={(merchantId) => update({ merchantId })} />
                        </div>
                        <div className="col-sm-6 col-lg-2">
                            <label className="form-label small text-muted mb-1" htmlFor="attemptChannel">Canal</label>
                            <select id="attemptChannel" className="form-select" value={filters.channel} onChange={e => update({ channel: e.target.value })}>
                                <option value="">Todos</option>
                                {Object.entries(CHANNEL).map(([code, info]) => <option key={code} value={code}>{info.label}</option>)}
                            </select>
                        </div>
                        <div className="col-sm-6 col-lg-2">
                            <label className="form-label small text-muted mb-1" htmlFor="attemptStatus">Estado</label>
                            <select id="attemptStatus" className="form-select" value={filters.status} onChange={e => update({ status: e.target.value })}>
                                <option value="">Todos</option>
                                {filters.status.includes(',') && <option value={filters.status}>Varios (resumen)</option>}
                                {Object.entries(STATUS).map(([code, info]) => <option key={code} value={code}>{info.label}</option>)}
                            </select>
                        </div>
                        <form className="col-lg-2" onSubmit={e => { e.preventDefault(); update({ reference: reference.trim() }); }}>
                            <label className="form-label small text-muted mb-1" htmlFor="attemptReference">Referencia</label>
                            <div className="input-group">
                                <input id="attemptReference" className="form-control" placeholder="Id, orden, link o cliente"
                                    value={reference} onChange={e => setReference(e.target.value)} />
                                <button type="submit" className="btn btn-outline-secondary" aria-label="Buscar"><i className="bi bi-search" /></button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* RESUMEN */}
            <div className="row g-2 mb-3">
                {tiles.map(tile => (
                    <div key={tile.label} className="col-6 col-md-4 col-xl-2">
                        <button type="button" onClick={() => update(tile.filter)} aria-pressed={isActive(tile.filter)}
                            className={`card border-0 shadow-sm h-100 w-100 text-start ${isActive(tile.filter) ? 'border-start border-4 border-primary' : ''}`}>
                            <div className="card-body py-2">
                                <div className="text-muted small">{tile.label}</div>
                                <div className={`fs-4 fw-semibold ${tile.tone}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{tile.value}</div>
                            </div>
                        </button>
                    </div>
                ))}
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {/* LISTADO */}
            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    {loading ? (
                        <div className="text-center py-5"><span className="spinner-border text-primary" /></div>
                    ) : !data || data.attempts.length === 0 ? (
                        <div className="text-muted text-center py-5">No hay intentos de pago con estos filtros.</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover table-sm align-middle mb-0">
                                <thead>
                                    <tr className="text-muted small text-uppercase">
                                        <th>Fecha</th>
                                        <th>Comercio</th>
                                        <th>Canal</th>
                                        <th className="text-end">Monto</th>
                                        <th>Tarjeta</th>
                                        <th>Estado</th>
                                        <th>Acción posterior</th>
                                        <th>Detalle</th>
                                    </tr>
                                </thead>
                                <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {data.attempts.map(a => (
                                        <tr key={a.attemptKey} role="button" onClick={() => setSelected(a.attemptKey)}>
                                            <td className="text-nowrap">{formatDate(a.createdAt)}</td>
                                            <td>{a.merchantName ?? (a.merchantId ? `#${a.merchantId}` : '-')}</td>
                                            <td className="text-nowrap">
                                                <i className={`bi ${CHANNEL[a.channel]?.icon ?? 'bi-credit-card'} me-1 text-primary`} />
                                                {CHANNEL[a.channel]?.label ?? a.channel}
                                                {a.operation === 'preAuth' && <span className="badge text-bg-light border ms-1">Preautorización</span>}
                                            </td>
                                            <td className="text-end text-nowrap">{formatAmount(a.amount, a.currency)}</td>
                                            <td className="text-nowrap">{a.pan ?? '-'}</td>
                                            <td><StatusBadge status={a.status} /></td>
                                            <td><FollowUpBadge status={a.followUpStatus} /></td>
                                            <td className="small text-muted" style={{ maxWidth: 280 }}>
                                                <div className="text-truncate">{a.errorMessage ?? a.description ?? ''}</div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <span className="small text-muted">{data ? `${data.total} intento(s)` : ''}</span>
                        {totalPages > 1 ? <Paginator totalPage={totalPages} pageActual={page} onChange={(actual) => setPage(actual)} /> : <div />}
                    </div>
                </div>
            </div>

            {selected && <AttemptDetail attemptKey={selected} onClose={() => setSelected(null)} />}
        </div>
    );
};

// Panel lateral con los datos del intento y su historial paso a paso.
const AttemptDetail = ({ attemptKey, onClose }: { attemptKey: string; onClose: () => void }) => {
    const [attempt, setAttempt] = useState<(PaymentAttempt & { events: AttemptEvent[] }) | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        setAttempt(null);
        setError('');
        topApi.get(`/payment-attempts/${attemptKey}`)
            .then(resp => setAttempt(resp.data))
            .catch(e => setError(errorMessage(e)));
    }, [attemptKey]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const followUp = parseDetail(attempt?.followUpDetail ?? null);
    const fields: [string, React.ReactNode][] = attempt ? [
        ['Id del intento', <code>{attempt.attemptKey}</code>],
        ['Canal', CHANNEL[attempt.channel]?.label ?? attempt.channel],
        ['Operación', attempt.operation === 'preAuth' ? 'Preautorización' : 'Cobro'],
        ['Procesador', attempt.processor ?? '-'],
        ['Monto', formatAmount(attempt.amount, attempt.currency)],
        ['Tarjeta', attempt.pan ?? '-'],
        ['Descripción', attempt.description ?? '-'],
        ['Orden del procesador', attempt.orderId ? <code>{attempt.orderId}</code> : '-'],
        ['Autorización', attempt.authCode ?? '-'],
        ['Transacción Swift', attempt.transactionId ?? '-'],
        ['Mesa', attempt.tableId ?? '-'],
        ['Orden creada', attempt.localOrderId ?? '-'],
        ['Link de pago', attempt.linkToken ? <code className="text-break">{attempt.linkToken}</code> : '-'],
        ['Cliente (clientId)', attempt.clientId ? <code className="text-break">{attempt.clientId}</code> : '-'],
        ['Creado', formatDate(attempt.createdAt)],
        ['Finalizado', formatDate(attempt.finishedAt)],
    ] : [];

    return (
        <>
            <div className="modal-backdrop fade show" onClick={onClose} />
            <div className="position-fixed top-0 end-0 h-100 bg-body shadow-lg d-flex flex-column"
                style={{ width: 'min(560px, 100vw)', zIndex: 1055 }} role="dialog" aria-modal="true" aria-label="Detalle del intento de pago">
                <div className="d-flex justify-content-between align-items-center border-bottom px-3 py-2">
                    <h5 className="mb-0">Detalle del intento</h5>
                    <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
                </div>
                <div className="overflow-auto p-3" style={{ flex: 1 }}>
                    {error && <div className="alert alert-danger">{error}</div>}
                    {!attempt && !error && <div className="text-center py-5"><span className="spinner-border text-primary" /></div>}
                    {attempt && (<>
                        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                            <StatusBadge status={attempt.status} />
                            <span className="text-muted small">Último paso: {eventLabel(attempt.step)}</span>
                        </div>

                        {attempt.errorMessage && (
                            <div className={`alert ${attempt.status === 'PENDING' ? 'alert-warning' : 'alert-danger'} small py-2`}>
                                {attempt.errorCode && <strong className="me-1">{attempt.errorCode}:</strong>}{attempt.errorMessage}
                            </div>
                        )}

                        {attempt.followUpStatus === 'ERROR' && (
                            <div className="alert alert-danger small py-2">
                                <i className="bi bi-exclamation-triangle me-1" />
                                El cobro se aprobó, pero falló la acción posterior
                                {followUp?.event ? ` (${eventLabel(followUp.event)})` : ''}. Revise el detalle en el historial.
                            </div>
                        )}

                        <dl className="row small mb-4">
                            {fields.map(([label, value]) => (
                                <React.Fragment key={label}>
                                    <dt className="col-5 text-muted fw-normal">{label}</dt>
                                    <dd className="col-7 mb-1">{value}</dd>
                                </React.Fragment>
                            ))}
                        </dl>

                        <h6 className="mb-2">Historial</h6>
                        <ol className="list-unstyled border-start ps-3 mb-0">
                            {attempt.events.map((ev, i) => {
                                const detail = parseDetail(ev.detail);
                                const failed = ev.status && ['ERROR', 'DECLINED', 'EXPIRED'].includes(ev.status);
                                return (
                                    <li key={i} className="mb-3 position-relative">
                                        <span className={`position-absolute rounded-circle ${failed ? 'bg-danger' : 'bg-primary'}`}
                                            style={{ width: 10, height: 10, left: -21, top: 6 }} />
                                        <div className="d-flex flex-wrap gap-2 align-items-baseline">
                                            <strong className="small">{eventLabel(ev.event)}</strong>
                                            {ev.status && (STATUS[ev.status] ? <StatusBadge status={ev.status} /> : <FollowUpBadge status={ev.status} />)}
                                        </div>
                                        <div className="text-muted small">{formatDate(ev.createdAt)}</div>
                                        {detail && (
                                            <pre className="small bg-body-tertiary rounded p-2 mt-1 mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {typeof detail === 'string' ? detail : JSON.stringify(detail, null, 2)}
                                            </pre>
                                        )}
                                    </li>
                                );
                            })}
                        </ol>
                    </>)}
                </div>
            </div>
        </>
    );
};
